import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from '@/components/error_boundary.tsx';
import useAppStore from '../../store/rootStore.ts';
import { useBridgeCheckoutStore } from './bridge_checkout_store';

const FunkitHost = lazy(() => import('./funkit_host'));

/**
 * Keeps @funkit/connect out of the entry bundle: the host chunk is prefetched on idle (so the
 * first Bridge click opens instantly) and only mounted on the first bridge request. Lives in its
 * own leaf component so bridge-request state changes don't re-render the provider stack.
 */
export function FunBridgeGate() {
  const funBridgeEnabled = useAppStore((state) => !!state.config?.features?.funBridgeEnabled);
  const bridgeRequested = useBridgeCheckoutStore((state) => state.request !== null);

  useEffect(() => {
    if (!funBridgeEnabled) return;
    const warm = () => void import('./funkit_host');
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(warm, { timeout: 10_000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timeoutId = window.setTimeout(warm, 3_000);
    return () => window.clearTimeout(timeoutId);
  }, [funBridgeEnabled]);

  if (!funBridgeEnabled || !bridgeRequested) {
    return null;
  }

  return (
    <ErrorBoundary message="The bridge failed to load. Please refresh and try again.">
      <Suspense fallback={null}>
        <FunkitHost />
      </Suspense>
    </ErrorBoundary>
  );
}
